import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import List from '@meitu/saas-ui/es/list';
import message from '@meitu/saas-ui/es/message';
import { formatColor, GradientColorResult } from '@meitu/saas-colors';
import { imageConverter, isWebpImage, loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio } from '@meitu/saas-editor-core/es/utils/layer-kit';
import {
    DynamicType,
    IBackgroundLayerProps,
    ILayerProps,
    StretchOptionType,
} from '@meitu/saas-editor-core/es/version-types';
import ColorGroupWithTitle from '@meitu/saas-editor-ui/es/components/color-group-with-title';
import { MaterialCard } from '@meitu/saas-editor-ui/es/components/material-card';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { fetchBackgrounds } from '@/services/background';
import { BackgroundListItem } from '@/services/material/index.d';
import { checkShowSvipCorner } from '@/utils/ab-test';
import './background-collapse.less';
import { IBackgroundLayerUpdateProps } from '@meitu/saas-editor-core/es/layers/background-layer/type';

export interface ICategory {
    name?: string;
    id?: string | number;
}
interface ISelectColor {
    layer: ILayerProps | null;
    color: string;
    setColor: Function;
    current?: null | number;
    setCurrent?: Function;
}

interface IListParams {
    cursor?: string;
}

export const IsFromOtherEntrance = 'IsFromOtherEntrance';
const WidthCard = 79;

const TitleNode: React.FC<ISelectColor> = React.memo(({ layer, color }) => {
    const { $t } = useLocaleStore();
    const cacheBackgroundLayer = useRef<ILayerProps | null>(layer);
    const leftNode = <span className="container-title__left">{$t((v) => v.collapse.backgroundCollapse.title)}</span>;

    const dealColor = useMemo(() => {
        return color ? (color === 'transparent' ? '#ffffff00' : color) : '#ffffff01'; // HACK: 默认颜色为透明而又不能选中完全透明
    }, [color]);

    return (
        <ColorGroupWithTitle
            isGradient={true}
            color={dealColor}
            setColor={(color, transparent) => {
                cacheBackgroundLayer.current = layer;
                if (transparent) {
                    editor.backgroundLayer.setBackgroundColor(
                        {
                            url: '',
                            backgroundColor: '',
                            opacity: 0,
                        },
                        false,
                    );
                } else {
                    if (color.includes('linear-gradient')) {
                        editor.backgroundLayer.setBackgroundGradient(
                            {
                                url: '',
                                backgroundColor: '',
                                backgroundGradient: formatColor(color) as GradientColorResult,
                            },
                            false,
                        );
                    } else {
                        editor.backgroundLayer.setBackgroundColor(
                            {
                                url: '',
                                backgroundColor: color,
                            },
                            false,
                        );
                    }
                }
            }}
            setColorAfter={(color, transparent, { isBrandColor }) => {
                if (transparent) {
                    editor.backgroundLayer.setBackgroundColor(
                        {
                            url: '',
                            backgroundColor: '#ffffff00',
                            opacity: 0,
                        },
                        true,
                        cacheBackgroundLayer.current!,
                    );
                } else {
                    if (color.includes('linear-gradient')) {
                        editor.backgroundLayer.setBackgroundGradient(
                            {
                                url: '',
                                backgroundColor: '',
                                backgroundGradient: formatColor(color) as GradientColorResult,
                            },
                            true,
                            cacheBackgroundLayer.current!,
                        );
                    } else {
                        editor.backgroundLayer.setBackgroundColor(
                            {
                                url: '',
                                backgroundColor: color,
                            },
                            true,
                            cacheBackgroundLayer.current!,
                        );
                    }
                }
            }}
            popupTriggerSize={{
                width: 68,
                height: 26,
            }}
            title={leftNode}
        />
    );
});

const BackgroundCollapse = () => {
    const { $t } = useLocaleStore();
    const [current, setCurrent] = useState<number | null>(null);
    const [color, setColor] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [cursor, setCursor] = useState<string>('');
    const [list, setList] = useState<BackgroundListItem[]>([]);
    const [layer, setLayer] = useState<ILayerProps | null>(editor.backgroundLayer.getBackgroundLayer());
    const [currentSize, setCurrentSize] = useState(editor.services.frame.getActiveFrameSize());
    const { width: stageWidth, height: stageHeight } = currentSize;

    useEffect(() => {
        const handle = editor.services.frame.onActiveIndexChange((stageIndex) => {
            setLayer(editor.backgroundLayer.getBackgroundLayer(stageIndex));
            setCurrentSize(editor.services.frame.getActiveFrameSize());
        });

        return () => {
            editor.off(handle);
        };
    }, []);

    useEffect(() => {
        const handler = editor.services.flatLayer.onChange(() => {
            const backgroundLayer = editor.backgroundLayer.getBackgroundLayer();
            setLayer(backgroundLayer);
            setCurrent(Number(editor.backgroundLayer.getBackgroundMaterialId(backgroundLayer)));
        }, layer?.id);

        return () => {
            editor.off(handler);
        };
    }, [layer?.id]);

    useEffect(() => {
        const handle = editor.services.stage.onResize(() => {
            setCurrentSize(editor.services.frame.getActiveFrameSize());
        });
        return () => {
            editor.off(handle);
        };
    }, []);
    const {
        backgroundColor,
        backgroundGradient = { colors: [], angle: 90 },
        url,
    } = (layer?.data as IBackgroundLayerProps) || {
        backgroundColor: '',
        url: '',
        backgroundGradient: { colors: [], angle: 90 },
    };
    const cacheHasUrl = useRef<boolean>(!!url);
    const selectHandler = async (value: BackgroundListItem) => {
        try {
            editor.services.mask.show({ loading: true });
            const { material: url } = value.material_conf;

            const isWebp = await isWebpImage(url);
            const { width, height } = await loadImageAsync(url);
            const { widthRatio, heightRatio } = computerBgRatio({
                imgWidth: width,
                imgHeight: height,
                stageHeight,
                stageWidth,
            });
            const stretchOption = value.picture_display_mode === 2 ? StretchOptionType.repeat : StretchOptionType.cover;
            const option = {
                url,
                backgroundColor: '',
                id: value.id,
                naturalHeight: height,
                naturalWidth: width,
                imageTransform: {
                    offsetX: 0,
                    offsetY: 0,
                    rotate: 0,
                    scale: 1,
                    flipH: false,
                    flipV: false,
                    widthRatio: stretchOption === StretchOptionType.repeat ? 1 : widthRatio,
                    heightRatio: stretchOption === StretchOptionType.repeat ? 1 : heightRatio,
                },
                stretchOption,
            } as IBackgroundLayerUpdateProps;

            if (isWebp) {
                option.dynamicInfo = {
                    name: '',
                    hidden: false,
                    dynamicType: DynamicType.Webp,
                    screenshotUrl: '',
                    enableBGColor: true,
                };

                if (value.material_conf?.defaultColor) {
                    option.backgroundColor = value.material_conf.defaultColor;
                }
            }
            editor.backgroundLayer.addBackgroundUrl(option);
            editor.services.mask.hide();
        } catch (e) {
            editor.services.mask.hide();
            message.error($t((v) => v.collapse.backgroundCollapse.messageError.applyFailed));
        }
    };

    // 获取列表
    const fetchList = async (params: IListParams) => {
        setLoading(true);
        const {
            data: {
                response: { materials, cursor: newCursor },
            },
        } = await fetchBackgrounds({ ...params, limit: 50 });
        if (newCursor) setCursor(newCursor);
        else setHasMore(false);
        setList((list) => {
            return list.concat(materials as BackgroundListItem[]);
        });
        setLoading(false);
    };
    useEffect(() => {
        fetchList({ cursor: '' });
    }, []);

    const nextPageRequest = () => {
        hasMore && !loading && fetchList({ cursor });
    };

    useEffect(() => {
        // 处理同时存在图片和颜色的异常模板
        if (cacheHasUrl.current) {
            cacheHasUrl.current = false;
            return;
        }
        if (backgroundGradient && backgroundGradient?.colors?.length > 0) {
            setColor(formatColor(backgroundGradient) as string);
        } else {
            setColor(backgroundColor);
        }
    }, [backgroundColor, backgroundGradient]);

    const formatImageUrl = (url: string) => {
        if (!url) return url;
        const [originUrl, _] = url.split('!');
        return originUrl;
    };

    return (
        <div
            className={classnames('c-background-container')}
            style={{
                height: '100%',
            }}
        >
            <div className="c-background-all-container">
                <TitleNode {...{ color, setColor, layer }} />
                <List<BackgroundListItem>
                    bodyClassName="m-material-list "
                    loading={loading}
                    hasMore={hasMore}
                    dataSource={list}
                    nextPageRequest={nextPageRequest}
                    id="backgroundCollapseDetails"
                    isWaterfall
                    columns={3}
                    getItemHeight={(item) =>
                        item.thumbnail_height ? (WidthCard * item.thumbnail_height) / item.thumbnail_width! : WidthCard
                    }
                    renderItem={(data: BackgroundListItem) => {
                        return (
                            <div
                                key={data.id}
                                className={classnames('container-list__item', {
                                    active: data.id === current && !backgroundColor,
                                })}
                            >
                                <MaterialCard
                                    key={data.id}
                                    width={79}
                                    src={imageConverter(formatImageUrl(data.thumbnail), 320)}
                                    cornerSrc={checkShowSvipCorner() ? data.corner_mark : ''}
                                    cornerPlacement="topRight"
                                    onClick={() => selectHandler(data)}
                                    cardStyle={{
                                        height: data.thumbnail_height
                                            ? (WidthCard / data.thumbnail_width!) * data.thumbnail_height
                                            : 'auto',
                                        marginBottom: '6px',
                                    }}
                                />
                            </div>
                        );
                    }}
                />
            </div>
        </div>
    );
};
export default React.memo(BackgroundCollapse);
